/**
 * Identity squares — initials and their fill colour.
 *
 * The mockup (2a/2b) cycles assignee avatars through four identity fills rather
 * than painting every square the same ink. With one fill, a column of avatars
 * reads as a single grey block and the assignee column carries no signal at a
 * glance; with four, you can pick out "the ones assigned to Divya" while
 * scanning. The colour is decorative — the name is always beside it — so a
 * stable hash is enough and no accessible meaning rides on it.
 */

/**
 * A person's name as it should be shown. Accounts created with a placeholder
 * in a required name field ("Jp" + "-") otherwise render as "Jp -" with the
 * initials "J-", so words made only of punctuation are dropped.
 * "Jp -" → "Jp"; "Mary-Jane O'Neil" is left alone.
 */
export function personName(name?: string | null): string {
  if (!name) return '';
  return name
    .trim()
    .split(/\s+/)
    .filter((word) => /[\p{L}\p{N}]/u.test(word))
    .join(' ');
}

/** "Rohit Menon" → "RM". Falls back to a single glyph. */
export function initials(name?: string): string {
  const shown = personName(name);
  if (!shown) return '·';
  const parts = shown.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '·';
}

/** The four identity fills, matching the mockup's rotation. */
const IDENTITY_FILLS = [
  'var(--co-avatar)',
  'var(--co-green)',
  'var(--co-burgundy)',
  'var(--co-brass)',
] as const;

/**
 * Deterministic fill for a person, so the same name always gets the same
 * square across the list, the board and the ticket header.
 */
export function identityColor(name?: string): string {
  if (!name) return 'var(--co-line-strong)';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return IDENTITY_FILLS[hash % IDENTITY_FILLS.length];
}
